import './src/styles/reset.css'

import { ContentfulLivePreviewProvider } from '@contentful/live-preview/react'
import { ThemeProvider } from '@emotion/react'
import { navigate } from 'gatsby'
import React from 'react'
import ReactGA from 'react-ga'
import { Helmet, HelmetProvider } from 'react-helmet-async'

import { LanguagePreferenceGate, markHydrated } from './src/components/LanguagePreferenceGate'
import Layout from './src/components/Layout'
import { AnonymousIdContextProvider } from './src/context/AnonymousIdContextProvider'
import { LocaleContextProvider } from './src/context/LocaleContextProvider'
import { PageContextProvider } from './src/context/PageContextProvider'
import { PathnameProvider } from './src/context/PathnameProvider'
import { SplitProvider } from './src/context/SplitProvider'
import { SEOProvider } from './src/context/useSEO'
import { ContentfulPreviewRefreshBanner } from './src/components/ContentfulPreviewRefreshBanner'
import { isContentfulPreviewFrame } from './src/helpers/isContentfulPreviewFrame'
import { useBootstrapMissingPreviewPage } from './src/hooks/useBootstrapMissingPreviewPage'
import { isProductionEnvironment } from './src/helpers/utils'
import { withQueryAndHash } from './src/helpers/urlFunctions'
import { AnalyticsService } from './src/services/analyticsService'
import { paramsService } from './src/services/paramsService'
import { baseTheme } from './src/styles/theme'

export const onClientEntry = () => {
  // Hosted develop only. Gatsby's `/___services` advertises developstatusserver on a
  // random container port; the browser then opens hostname:thatPort/socket.io.
  // Azure does not expose it. Strip the service so the client never connects.
  // Must wrap fetch here: Gatsby calls `/___services` after onClientEntry.
  if (process.env.GATSBY_PREVIEW_HOSTED === 'true') {
    const fetchImpl = window.fetch.bind(window)
    // Split RUM (`rum-agent`) wraps fetch with `apply(null, args)`. Call through as a
    // `function` bound to `window` so other POSTs (refresh, overlay) are not illegal-invocation.
    window.fetch = function (...args) {
      const input = args[0]
      const url = typeof input === 'string' ? input : input && input.url
      if (typeof url === 'string' && url.includes('/___services')) {
        return fetchImpl.apply(window, args).then(res =>
          res.json().then(services => {
            delete services.developstatusserver
            return new Response(JSON.stringify(services), {
              status: res.status,
              headers: { 'Content-Type': 'application/json' },
            })
          }),
        )
      }
      return fetchImpl.apply(window, args)
    }
  }

  // Preview iframe sessions must not pollute GA / App Insights; params parsing still runs.
  if (!isContentfulPreviewFrame()) {
    initGoogleAnalytics()
    const appInsightsInstrumentationKey = process.env.APP_INSIGHTS_INSTRUMENTATION_KEY
    if (appInsightsInstrumentationKey) AnalyticsService.init(appInsightsInstrumentationKey)
  }
  paramsService.init(window.location.href)
}

// From here on, LanguagePreferenceGate redirects on client-side navigation (the first load is the inline script's job).
export const onInitialClientRender = () => markHydrated()

export const onRouteUpdate = ({ location }) => {
  if (isContentfulPreviewFrame()) return

  const sanitizedUrl = sanitizeUrl(location.pathname)
  AnalyticsService.trackPageView({ name: sanitizedUrl })
}

// Hosts missing-page bootstrap and in-flight refresh banner (manual refresh only — no save listener).
const ContentfulPreviewRefresher = () => {
  useBootstrapMissingPreviewPage()
  return <ContentfulPreviewRefreshBanner />
}

// Only wired up on the browser side: the SDK talks to the Contentful editor over postMessage.
// The provider calls `init` while rendering, and `init` throws when it cannot trace the parent
// frame back to a Contentful origin, which is what happens under Cypress. Mounting it only for the
// editor's preview iframe keeps the SDK out of every other way the app gets loaded.
export const wrapRootElement = ({ element }) => {
  const app = (
    <ThemeProvider theme={baseTheme}>
      <SEOProvider>
        <HelmetProvider>
          <AnonymousIdContextProvider>
            <PageContextProvider>{element}</PageContextProvider>
          </AnonymousIdContextProvider>
        </HelmetProvider>
      </SEOProvider>
    </ThemeProvider>
  )

  if (!isContentfulPreviewFrame()) return app

  return (
    <ContentfulLivePreviewProvider
      // The environment's English code (en-US or en-CA), defined in gatsby-node.js.
      locale={process.env.CONTENTFUL_DEFAULT_LOCALE}
      enableInspectorMode={false}
      enableLiveUpdates
      debugMode={!isProductionEnvironment()}
    >
      <ContentfulPreviewRefresher />
      {app}
    </ContentfulLivePreviewProvider>
  )
}

// Component for running page level experiments, gets rerendered on each navigation event
const PageExperiments = ({ pathname }) => {
  return null
}
export const wrapPageElement = ({ element, props }) => {
  // props provide same data to Layout as Page element will get
  // including location, data, etc - you don't need to pass it

  // Draft pages can have `sections: null` until editors add any (Preview API).
  const sectionWithRedirect = props?.data?.contentfulPage?.sections?.find(
    section => section.__typename === 'ContentfulMetadata' && section.redirect === true,
  )

  if (sectionWithRedirect) {
    const redirectTo = withQueryAndHash(
      sectionWithRedirect.redirectUrl ?? '/',
      window.location.search,
      window.location.hash,
    )
    redirectTo.startsWith('https://') ? (window.location = redirectTo) : navigate(redirectTo, { replace: true })
  }

  const SHOULD_NOT_INDEX = !isProductionEnvironment() || String(process.env.GENERATE_METADATA).toLowerCase() !== 'true'

  const pathname = props.path

  // Fallback to current url if canonical link is not provided
  const currentUrl = props.href

  return sectionWithRedirect ? (
    <Helmet>
      <link rel="canonical" href={sectionWithRedirect?.canonicalLink ?? currentUrl} />
    </Helmet>
  ) : (
    <SplitProvider>
      <Helmet>{SHOULD_NOT_INDEX && <meta name="robots" content="noindex" />}</Helmet>
      <LocaleContextProvider locale={props.pageContext?.locale} alternates={props.pageContext?.alternates}>
        <LanguagePreferenceGate locale={props.pageContext?.locale} alternates={props.pageContext?.alternates ?? {}}>
          <PathnameProvider pathname={pathname}>
            <PageExperiments pathname={pathname} />
            <Layout {...props}>{element}</Layout>
          </PathnameProvider>
        </LanguagePreferenceGate>
      </LocaleContextProvider>
    </SplitProvider>
  )
}

/**********************************
 *
 * @description private functions
 *
 *********************************/

function sanitizeUrl(originalUrl) {
  const LAST_SLASH_REGEX = /\/$/
  if (originalUrl.length === 1) {
    return originalUrl
  }
  return originalUrl.replace(LAST_SLASH_REGEX, '')
}

function initGoogleAnalytics() {
  if (process.env.GA_TOKEN) {
    ReactGA.initialize(process.env.GA_TOKEN)
  } else {
    console.error('Please configure your Google Analytics to Track Events!')
  }
}
